import React, { useState } from 'react';
import './AWSSetup.css';

const BACKEND_URL = 'https://spectraine-backend.onrender.com';

function AWSSetup() {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [cfData, setCfData] = useState(null);
  const [roleArn, setRoleArn] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const generateCloudFormationUrl = async () => {
    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');

      const response = await fetch(`${BACKEND_URL}/generate-aws-setup`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      console.log('CF DATA:', JSON.stringify(data));

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to generate CloudFormation URL');
      }

      setCfData(data);
      setStep(2);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const connectAWS = async (e) => {
  e.preventDefault();
  setLoading(true);
  setError('');
  try {
    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('role_arn', roleArn);
    formData.append('external_id', cfData?.external_id || '');

    const url = `${BACKEND_URL}/connect-aws-cloudformation`;
    console.log('Connecting AWS to URL:', url);

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      body: formData
    });

    const data = await response.json();
    console.log('AWS connect response:', response.status, data);

    if (!response.ok) {
      throw new Error(data.detail || 'Failed to connect AWS account');
    }

    setSuccess(true);
    setStep(3);
    setTimeout(() => { window.location.href = '/#/dashboard' }, 2000);
  } catch (err) {
    console.error('AWS connect error:', err);
    setError(err.message);
  } finally {
    setLoading(false);
  }
};

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <div className="aws-setup-container">
      {/* Animated Background */}
      <div className="cyber-grid"></div>
      <div className="glow-orb orb-1"></div>
      <div className="glow-orb orb-2"></div>

      {/* Header */}
      <div className="setup-header">
        <h1 className="cyber-title">
          <span className="text-glow">AWS</span> Integration
        </h1>
        <p className="cyber-subtitle">Secure Cross-Account Access Protocol</p>
      </div>

      {/* Progress Bar */}
      <div className="progress-bar">
        <div className={`progress-step ${step >= 1 ? 'active' : ''}`}>
          <div className="step-number">01</div>
          <div className="step-label">Generate</div>
        </div>
        <div className={`progress-line ${step >= 2 ? 'active' : ''}`}></div>
        <div className={`progress-step ${step >= 2 ? 'active' : ''}`}>
          <div className="step-number">02</div>
          <div className="step-label">Deploy</div>
        </div>
        <div className={`progress-line ${step >= 3 ? 'active' : ''}`}></div>
        <div className={`progress-step ${step >= 3 ? 'active' : ''}`}>
          <div className="step-number">03</div>
          <div className="step-label">Connect</div>
        </div>
      </div>

      {/* Main Content */}
      <div className="setup-content">

        {/* STEP 1: Generate CloudFormation */}
        {step === 1 && (
          <div className="setup-card fade-in">
            <div className="card-icon">🚀</div>
            <h2>Initialize AWS Integration</h2>
            <p className="card-description">
              Deploy a secure IAM role in your AWS account with read-only permissions
              for security scanning and cost optimization analysis.
            </p>

            <div className="features-grid">
              <div className="feature-item">
                <span className="feature-icon">🔒</span>
                <span>Read-only Security Audit</span>
              </div>
              <div className="feature-item">
                <span className="feature-icon">💰</span>
                <span>Cost & Usage Analysis</span>
              </div>
              <div className="feature-item">
                <span className="feature-icon">🛡️</span>
                <span>Zero Write Permissions</span>
              </div>
              <div className="feature-item">
                <span className="feature-icon">⚡</span>
                <span>One-Click Deployment</span>
              </div>
            </div>

            {error && (
              <div className="alert alert-error">
                <span className="alert-icon">⚠️</span>
                {error}
              </div>
            )}

            <button 
              className="cyber-button primary"
              onClick={generateCloudFormationUrl}
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="spinner"></span>
                  Generating...
                </>
              ) : (
                <>
                  <span className="button-icon">⚡</span>
                  Generate CloudFormation Link
                </>
              )}
            </button>
          </div>
        )}

        {/* STEP 2: Deploy CloudFormation */}
        {step === 2 && cfData && (
          <div className="setup-card fade-in">
            <div className="card-icon">☁️</div>
            <h2>Deploy IAM Role</h2>
            <p className="card-description">
              Click the button below to open AWS CloudFormation Console.
              Review the permissions and create the stack.
            </p>

            <div className="info-box">
              <div className="info-row">
                <span className="info-label">External ID:</span>
                <div className="info-value-container">
                  <code className="info-value">{cfData.external_id}</code>
                  <button 
                    className="copy-btn"
                    onClick={() => copyToClipboard(cfData.external_id)}
                  >
                    📋
                  </button>
                </div>
              </div>
              <div className="info-row">
                <span className="info-label">Template:</span>
                <code className="info-value small">{cfData.template_url}</code>
              </div>
            </div>

            <div className="instructions">
              <h3>Instructions:</h3>
              <ol>
                <li>Click the button to open AWS Console</li>
                <li>Review the IAM role permissions (read-only)</li>
                <li>Click <strong>"Create stack"</strong> button</li>
                <li>Wait 1-2 minutes for stack to complete</li>
                <li>Go to <strong>"Outputs"</strong> tab</li>
                <li>Copy the <strong>"RoleARN"</strong> value</li>
                <li>Return here and paste it below</li>
              </ol>
            </div>

            {error && (
              <div className="alert alert-error">
                <span className="alert-icon">⚠️</span>
                {error}
              </div>
            )}

            <div className="button-group">
              <a 
                href={cfData.cloudformation_console_url}
                target="_blank"
                rel="noopener noreferrer"
                className="cyber-button primary"
              >
                <span className="button-icon">🚀</span>
                Open AWS Console
              </a>
              <button 
                className="cyber-button secondary"
                onClick={() => setStep(3)}
              >
                I've Deployed the Stack →
              </button>
            </div>
          </div>
        )}

        {/* STEP 3: Connect AWS */}
        {step === 3 && !success && (
          <div className="setup-card fade-in">
            <div className="card-icon">🔗</div>
            <h2>Connect Your Account</h2>
            <p className="card-description">
              Paste the Role ARN from CloudFormation stack outputs to complete the integration.
            </p>

            <form onSubmit={connectAWS}>
              <div className="form-group">
                <label>AWS Role ARN</label>
                <input
                  type="text"
                  className="cyber-input"
                  placeholder="arn:aws:iam::123456789012:role/SpectraineIntegrationRole"
                  value={roleArn}
                  onChange={(e) => setRoleArn(e.target.value)}
                  required
                />
                <span className="input-hint">
                  Format: arn:aws:iam::ACCOUNT_ID:role/ROLE_NAME
                </span>
              </div>

              {error && (
                <div className="alert alert-error">
                  <span className="alert-icon">⚠️</span>
                  {error}
                </div>
              )}

              <div className="button-group">
                <button 
                  type="button"
                  className="cyber-button secondary"
                  onClick={() => setStep(2)}
                >
                  ← Back
                </button>
                <button 
                  type="submit"
                  className="cyber-button primary"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner"></span>
                      Verifying...
                    </>
                  ) : (
                    <>
                      <span className="button-icon">✓</span>
                      Connect AWS Account
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* SUCCESS */}
        {success && (
          <div className="setup-card fade-in success-card">
            <div className="success-icon">🎉</div>
            <h2>Integration Complete!</h2>
            <p className="card-description">
              Your AWS account has been successfully connected to Spectraine.
            </p>

            <div className="success-stats">
              <div className="stat-item">
                <div className="stat-value">✓</div>
                <div className="stat-label">Role Verified</div>
              </div>
              <div className="stat-item">
                <div className="stat-value">🔒</div>
                <div className="stat-label">Secure Access</div>
              </div>
              <div className="stat-item">
                <div className="stat-value">⚡</div>
                <div className="stat-label">Ready to Scan</div>
              </div>
            </div>

            <div className="info-box success">
              <p><strong>Next Steps:</strong></p>
              <ul>
                <li>First security scan runs tomorrow at 6:00 AM UTC</li>
                <li>You'll receive an email with findings</li>
                <li>View real-time insights in your dashboard</li>
                <li>Cost optimization recommendations will be generated</li>
              </ul>
            </div>

            <button 
              className="cyber-button primary"
              onClick={() => window.location.href = '/#/dashboard'}
            >
              <span className="button-icon">📊</span>
              Go to Dashboard
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default AWSSetup;
